import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import { personService } from '@shared/services/personService';
import api from '@shared/services/api';

export const useEmployerForm = (person) => {
    const { t } = useTranslation();
    const [employerData, setEmployerData] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editingEmployer, setEditingEmployer] = useState(null);
    const [countries, setCountries] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);

    const validationSchema = yup.object({
        startDate: yup.mixed()
            .test('is-required', t('validation.person.employer.startDateRequired'), function(value) {
                return value !== '' && value !== null && value !== undefined;
            })
            .test('not-future-date', t('validation.person.employer.startDateNotFuture'), function(value) {
                if (!value) return true;
                const startDate = new Date(value);
                const today = new Date();
                return startDate <= today;
            })
            .nullable(),
        endDate: yup.mixed()
            .test('is-required', t('validation.person.employer.endDateRequired'), function(value) {
                return value !== '' && value !== null && value !== undefined;
            })
            .test('not-future-date', t('validation.person.employer.endDateNotFuture'), function(value) {
                if (!value) return true;
                const endDate = new Date(value);
                const today = new Date();
                return endDate <= today;
            })
            .test('end-after-start', t('validation.person.employer.endDateAfterStart'), function(value) {
                const { startDate } = this.parent;
                if (!startDate || !value) return true;
                const endDate = new Date(value);
                const start = new Date(startDate);
                return endDate >= start;
            })
            .nullable(),
        position: yup.string()
            .required(t('validation.person.employer.positionRequired'))
            .max(60, t('validation.person.employer.positionMaxLength')),
        employer: yup.string()
            .required(t('validation.person.employer.employerRequired'))
            .max(60, t('validation.person.employer.employerMaxLength')),
        country: yup.string()
            .required(t('validation.person.employer.countryRequired'))
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        watch,
        clearErrors
    } = useForm({
        resolver: yupResolver(validationSchema)
    });

    const fetchEmployerData = async () => {
        try {
            if (person?.id) {
                const data = await personService.getJobHistory(person.id);
                const transformedData = data.map(item => ({
                    id: item.id,
                    startDate: item.fromDate,
                    endDate: item.toDate,
                    position: item.position,
                    employer: item.employerName,
                    country: item.country
                }));
                setEmployerData(transformedData);
            }
        } catch (error) {
            console.error('Error fetching employer data:', error);
            notifyError(t('persons.employer.fetchError'));
        }
    };

    const fetchCountries = async () => {
        try {
            const response = await api.get('/countries/list');
            const countryOptions = response.data.map(country => ({
                value: country.countryCode,
                label: country.countryNameBg
            }));
            setCountries(countryOptions);
        } catch (error) {
            console.error('Error fetching countries:', error);
        }
    };

    useEffect(() => {
        fetchEmployerData();
        fetchCountries();
    }, [person?.id]);

    const handleEdit = (employer) => {
        setIsEditing(true);
        setEditingEmployer(employer);
        setValue('startDate', employer.startDate);
        setValue('endDate', employer.endDate);
        setValue('position', employer.position);
        setValue('employer', employer.employer);
        setValue('country', employer.country);
        
        // Set the selected country for the Select component
        const countryOption = countries.find(c => c.label === employer.country);
        setSelectedCountry(countryOption);
    };

    const handleDelete = async (id) => {
        try {
            await personService.deleteJobHistory(id);
            notifySuccess(t('persons.employer.deleteSuccess'));
            await fetchEmployerData();
        } catch (error) {
            notifyError(t('persons.employer.deleteError'));
        }
    };

    const onSubmit = async (data) => {
        try {
            if (!person?.id) {
                notifyError(t('persons.employer.personIdRequired'));
                return;
            }

            const payload = {
                ...data,
                personId: person.id
            };

            if (isEditing && editingEmployer) {
                await personService.updateJobHistory(editingEmployer.id, payload);
                notifySuccess(t('persons.employer.updateSuccess'));
            } else {
                await personService.addJobHistory(payload);
                notifySuccess(t('persons.employer.addSuccess'));
            }
            
            handleCancel();
            await fetchEmployerData();
        } catch (error) {
            notifyError(isEditing 
                ? t('persons.employer.updateError')
                : t('persons.employer.addError')
            );
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditingEmployer(null);
        setSelectedCountry(null); // Reset selected country
        reset({
            startDate: '',
            endDate: '',
            position: '',
            employer: '',
            country: ''
        });
    };

    const handleCountryChange = (selectedOption) => {
        setValue('country', selectedOption.label, { shouldValidate: true });
        setSelectedCountry(selectedOption);
        clearErrors('country');
    };

    return {
        register,
        errors,
        handleSubmit,
        onSubmit,
        isEditing,
        employerData,
        handleEdit,
        handleDelete,
        handleCancel,
        countries,
        handleCountryChange,
        setValue,
        selectedCountry,
        watch
    };
}; 